import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ChevronRight, Shield, Star, Users, Heart, BookOpen } from 'lucide-react';

const ApostolicPropheticTrack = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Target className="w-8 h-8 text-white" />
            </div>
            <div className="text-sm font-semibold uppercase tracking-wider">Track 1</div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Apostolic & Prophetic Leadership Track
          </h1>
          <p className="text-xl text-purple-100 max-w-3xl">
            A 12-month formation program for those called to ministry, teaching, intercession, and prophetic leadership.
          </p>
        </div>
      </div>

      {/* Description & Outcomes */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Description</h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            This track is for individuals called to leadership in the Body of Christ—those who will carry prophetic intelligence, shepherd God's people, and steward spiritual authority with humility and wisdom. Over 12 months, you will be equipped to hear God clearly, lead with maturity, and minister with weight and accuracy.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-6">Program Outcomes</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Spiritual Maturity</h4>
                <p className="text-gray-600 text-sm">Gain wisdom, discernment, and spiritual grounding in the Word and the Spirit.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Star className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Prophetic Clarity</h4>
                <p className="text-gray-600 text-sm">Learn to hear and discern God's voice with accuracy and confidence.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Leadership Capacity</h4>
                <p className="text-gray-600 text-sm">Develop authority rooted in humility, servanthood, and the fear of the Lord.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Heart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Ministry Weight</h4>
                <p className="text-gray-600 text-sm">Carry the presence and authority needed to impact lives and shift atmospheres.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Modules */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Curriculum Modules</h2>
          
          {/* Stage 1 */}
          <div className="mb-12">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
              <h3 className="text-xl font-bold text-purple-900">Stage 1 — Awakening Prophetic Identity</h3>
            </div>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 1: Recognizing the Voice of God</h4>
                <p className="text-gray-600 text-sm">Learn to distinguish God's voice from your own thoughts and develop sensitivity to the Holy Spirit's leading.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 2: Discernment of Spirit & Source</h4>
                <p className="text-gray-600 text-sm">Master the ability to discern whether impressions come from God, self, or the enemy.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 3: Dreams, Visions & Prophetic Journaling</h4>
                <p className="text-gray-600 text-sm">Understand how God speaks through dreams and visions, and learn to record prophetic encounters accurately.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 4: Inner Healing & Spiritual Clarity</h4>
                <p className="text-gray-600 text-sm">Address wounds and strongholds that hinder prophetic clarity and spiritual perception.</p>
              </div>
            </div>
          </div>

          {/* Stage 2 */}
          <div className="mb-12">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
              <h3 className="text-xl font-bold text-purple-900">Stage 2 — Prophetic Intelligence & Spiritual Sight</h3>
            </div>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 5: Colors, Numbers, Symbols & Atmospheres</h4>
                <p className="text-gray-600 text-sm">Decode prophetic language and learn to interpret spiritual symbolism with biblical accuracy.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 6: Seer Dimensions & Visions of the Lord</h4>
                <p className="text-gray-600 text-sm">Develop seer capacity and learn to steward heavenly visions with wisdom and humility.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 7: Prophetic Deliverance & Healing Ministry</h4>
                <p className="text-gray-600 text-sm">Operate in prophetic deliverance and bring healing through Holy Spirit-led ministry.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 8: Timing, Tone & Administrative Wisdom</h4>
                <p className="text-gray-600 text-sm">Learn when to speak, how to deliver prophetic words, and understand the order of prophetic administration.</p>
              </div>
            </div>
          </div>

          {/* Stage 3 */}
          <div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
              <h3 className="text-xl font-bold text-purple-900">Stage 3 — Apostolic Formation & Sending</h3>
            </div>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 9: Apostolic Identity & Commissioning Mindset</h4>
                <p className="text-gray-600 text-sm">Understand your apostolic calling and cultivate the mindset of one who is sent by God.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 10: Territory, Assignment & Spheres of Influence</h4>
                <p className="text-gray-600 text-sm">Discover your assigned territory and learn to steward your sphere of influence with Kingdom authority.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 11: Prophetic Teams, Watchmen & Intercessory Order</h4>
                <p className="text-gray-600 text-sm">Build and lead prophetic teams, establish watchmen structures, and create intercessory order.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Module 12: Impartation, Activation & Commissioning</h4>
                <p className="text-gray-600 text-sm">Learn to impart prophetic gifts, activate others in their calling, and commission leaders with apostolic authority.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-purple-900 to-purple-700 rounded-3xl p-12 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Target className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Ready to Begin Your Journey?</h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Join the Apostolic & Prophetic Leadership Track and be formed into a leader who carries the weight of God.
            </p>
            <div className="flex items-center justify-center space-x-6 mb-8">
              <div>
                <div className="text-sm text-purple-200">Duration</div>
                <div className="text-2xl font-bold">12 Months</div>
              </div>
              <div className="w-px h-12 bg-purple-500"></div>
              <div>
                <div className="text-sm text-purple-200">Investment</div>
                <div className="text-2xl font-bold">From £120/mo</div>
                <div className="text-xs text-purple-200">or £1,200 upfront</div>
              </div>
            </div>
            <button
              onClick={() => navigate('/pricing?track=apostolic-prophetic')}
              className="bg-white text-purple-900 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-all duration-200 inline-flex items-center space-x-2 group"
            >
              <span>View Payment Plans</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApostolicPropheticTrack;